import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Col,
  Input,
  Popover,
  PopoverBody,
  Row,
  Spinner,
} from "reactstrap";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import moment from "moment";
import exportFromJSON from "export-from-json";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import MenuModal from "./MenuModal";
import ConfirmationModal from "components/Modals/ConfirmationModal";
import SwitchComponent from "components/shared/Switch";
import {
  reorderParentMenus,
  fetchMenus,
  deleteMenu,
  toggleMenuVisibility,
  fetchMenuCategoriesAndItems,
} from "store/actions/menuActions";
import ItemModal from "./Categories/Items/ItemModal";
import { fetchCategories } from "store/actions/categoriesActions";
import usePermissions from "hooks/usePermissions";

const Menu = () => {
  const dispatch = useDispatch();
  const popoverRef = useRef();
  const history = useHistory();
  const { user, userPermissions } = useSelector((state) => state.auth);

  const { menus } = useSelector((state) => state.menus);
  const { categories } = useSelector((state) => state.category);

  const {
    add: addAccess,
    edit: editAccess,
    delete: deleteAccess,
    export: exportAccess,
    visibility: visibilityAccess,
  } = usePermissions();

  const [menusData, setMenusData] = useState([]);
  const [search, setSearch] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [menuModal, setMenuModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(null);
  const [itemModal, setItemModal] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [error, setError] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryOpt, setCategoryOpt] = useState([]);
  const hasCategories = userPermissions?.menus?.category?.active;
  const menuOptions = menus.map((menu) => ({
    value: menu.id,
    label: menu.name,
    restaurantId: menu.restaurantID,
  }));

  useEffect(() => {
    const options = categories.map((category) => ({
      value: category.id,
      label: category.name,
    }));
    setCategoryOpt(options);
  }, [categories]);

  const handleMenuSelect = async (opt) => {
    setLoadingCategories(true);
    if (!opt) return;
    setSelectedMenu(opt);
    dispatch(fetchCategories(opt.restaurantId, opt.value)).then(() => {
      setError(true);
      setLoadingCategories(false);
    });
  };

  const togglePopover = (id) => {
    if (popoverOpen === id) {
      setPopoverOpen(null);
    } else {
      setPopoverOpen(id);
    }
  };

  const itemModalToggler = () => {
    setItemModal(!itemModal);
    setSelectedMenu(null);
    setSelectedCategory(null);
    setError(false);
    setCategoryOpt([]);
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearch(query);

    const filtered = menus.filter((menu) =>
      menu.name.toLowerCase().includes(query)
    );
    setMenusData(filtered);
  };

  const menuModalToggler = () => {
    if (menuModal) {
      setActiveMenu(null);
      setEditMode(false);
    }
    setMenuModal(!menuModal);
  };

  const confirmationModalToggler = () => {
    if (confirmationModal) {
      setActiveMenu(null);
    }
    setConfirmationModal(!confirmationModal);
  };

  const initiateEditMenu = (menu) => {
    setActiveMenu(menu);
    setEditMode(true);
    menuModalToggler();
  };

  const initiateDelete = (menu) => {
    setActiveMenu(menu);
    confirmationModalToggler();
  };

  const handleDeleteMenu = () => {
    setIsDeleting(true);
    dispatch(deleteMenu(activeMenu?.id))
      .then(() => confirmationModalToggler())
      .finally(() => setIsDeleting(false));
  };

  const handleToggleMenuVisibility = async (id, isVisible) => {
    let response = false;
    await dispatch(
      toggleMenuVisibility(id, isVisible, () => {
        response = true;
      })
    );
    setPopoverOpen(null);
    return response;
  };

  const handleExportMenu = async (menu) => {
    setIsExporting(true);
    try {
      if (menu) {
        const categoriesAndItems = await new Promise((resolve) => {
          dispatch(fetchMenuCategoriesAndItems(menu.id, resolve));
        });

        const csvData = [];

        categoriesAndItems?.forEach((Category) => {
          for (let items of Category.items) {
            let sizesArray = [];
            for (let variant of items.variants) {
              sizesArray.push(
                `${variant.size}_${variant.price}_${variant.calories}`
              );
            }
            let sizesString = sizesArray.join(",");

            csvData.push({
              category: Category?.name,
              itemName: items?.name,
              EstTime: items?.estimatedTime,
              Sizes_price_calories: sizesString,
            });
          }
        });

        const fileName = menu?.name;
        const exportType = exportFromJSON.types.csv;
        exportFromJSON({ data: csvData, fileName, exportType });
      }
    } catch (error) {
      console.error("Error exporting data:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    const items = Array.from(menusData);
    const [reorderedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      position: index,
    }));

    setMenusData(updatedItems);
    setIsReordering(true);

    const cleanedItems = updatedItems.map(({ hasItems, ...rest }) => rest);
    try {
      await dispatch(reorderParentMenus(cleanedItems));
    } catch (err) {
      console.error("Failed to reorder:", err);
      setMenusData(menusData);
    } finally {
      setIsReordering(false);
    }
  };

  useEffect(() => {
    setMenusData(menus);
  }, [menus]);

  useEffect(() => {
    if (user?.restaurantID) {
      setIsFetching(true);
      dispatch(fetchMenus(user?.restaurantID, () => setIsFetching(false)));
    }
  }, [dispatch, user?.restaurantID]);
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992 && popoverOpen) {
        setPopoverOpen(null);
      }
    };
    window.addEventListener("resize", handleResize);
  }, [popoverOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!popoverRef.current?.contains(e.target)) {
        setPopoverOpen(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <>
      <Row className="justify-content-end px-4 py-3 border-lighter border-bottom">
        <Col md="12" lg="4" className="mb-2">
          <Input
            placeholder="Search menu by name"
            value={search}
            onChange={handleSearch}
          />
        </Col>
        {addAccess && (
          <>
            <Col md="6" lg="4" xl="3" className="mb-2">
              <Button
                color="success"
                className="w-100"
                onClick={menuModalToggler}
              >
                Create New Menu
              </Button>
            </Col>
            <Col md="6" lg="4" xl="3" className="">
              <Button
                color="primary"
                className="w-100"
                onClick={() => setItemModal(true)}
              >
                Add Quick Item
              </Button>
            </Col>
          </>
        )}
      </Row>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="menus-container position-relative">
          {isReordering && (
            <div className="reorder-overlay">
              <Spinner color="light" />
            </div>
          )}
          {isFetching ? (
            <Row className="py-5 justify-content-center align-items-center">
              <Spinner color="primary" />
            </Row>
          ) : menusData.length > 0 ? (
            <Droppable droppableId="menus">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="my-4"
                >
                  {menusData?.map((menu, index) => (
                    <Draggable
                      key={menu?.id}
                      draggableId={menu?.id}
                      index={index}
                      isDragDisabled={!editAccess}
                    >
                      {(provided) => (
                        <div
                          key={menu?.id}
                          className="d-flex align-items-center justify-content-between bg-white rounded mx-3 mb-3  p-3 shadow-sm border"
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <div className="d-flex flex-column flex-sm-row w-100">
                            <Col
                              md="6"
                              lg="4"
                              xl="6"
                              className="d-flex align-items-center"
                            >
                              <div className="d-flex align-items-center justify-content-left w-100">
                                {editAccess && (
                                  <div className="d-flex align-items-center h-100">
                                    <i className="fas fa-2x fa-ellipsis-v"></i>
                                    <i className="fas fa-2x fa-ellipsis-v"></i>
                                  </div>
                                )}
                                <div className="ml-3 h-100 d-flex flex-column justify-content-space-evenly">
                                  <h2 className="mb-0 mt-2 text-capitalize">
                                    {menu?.name}
                                  </h2>
                                  <p className="text-muted">
                                    {moment
                                      .unix(menu?.createdAt?.seconds)
                                      .format("MMM DD, YYYY")}
                                  </p>
                                </div>
                              </div>
                            </Col>
                            <Col
                              md="6"
                              lg="8"
                              xl="6"
                              className="d-flex align-items-center"
                            >
                              <div className="d-flex justify-content-end w-100 align-items-center">
                                <div className="d-none d-lg-flex flex-wrap align-items-center">
                                  {visibilityAccess && (
                                    <div className="mr--3">
                                      <SwitchComponent
                                        id={menu?.id}
                                        key={menu?.id + menu?.isVisible}
                                        value={menu?.isVisible}
                                        onAction={(newState, event) => {
                                          return handleToggleMenuVisibility(
                                            menu?.id,
                                            newState
                                          );
                                        }}
                                      />
                                    </div>
                                  )}
                                  {exportAccess && (
                                    <Button
                                      className="bg-primary text-white border-primary mr-2 btn-sm"
                                      disabled={!menu?.hasItems}
                                      onClick={() => {
                                        setActiveMenu(menu);
                                        handleExportMenu(menu);
                                      }}
                                    >
                                      {isExporting &&
                                        menu?.id === activeMenu?.id && (
                                          <Spinner size="sm" className="mr-2" />
                                        )}
                                      Export
                                    </Button>
                                  )}
                                  {editAccess && (
                                    <Button
                                      size="sm"
                                      color="warning"
                                      className="mr-2"
                                      onClick={() => initiateEditMenu(menu)}
                                    >
                                      Edit
                                    </Button>
                                  )}
                                  {deleteAccess && (
                                    <Button
                                      size="sm"
                                      color="danger"
                                      className="mr-2"
                                      onClick={() => initiateDelete(menu)}
                                    >
                                      Delete
                                    </Button>
                                  )}
                                  {(hasCategories ||
                                    user?.type === "restaurant") && (
                                    <Button
                                      size="sm"
                                      color="success"
                                      onClick={() => {
                                        history.push(
                                          `/admin/menus/${menu?.id}/categories`
                                        );
                                      }}
                                    >
                                      View Categories
                                    </Button>
                                  )}
                                </div>

                                <div className="d-lg-none">
                                  <div className="d-flex">
                                    {visibilityAccess && (
                                      <div className="mt-2">
                                        <SwitchComponent
                                          id={menu?.id}
                                          key={menu?.id + menu?.isVisible}
                                          value={menu?.isVisible}
                                          onAction={(newState, event) => {
                                            return handleToggleMenuVisibility(
                                              menu?.id,
                                              newState
                                            );
                                          }}
                                        />
                                      </div>
                                    )}
                                    <Button
                                      id={`popover-${menu?.id}`}
                                      color="success"
                                      size="sm"
                                      className=""
                                      onClick={() => togglePopover(menu?.id)}
                                    >
                                      Actions
                                    </Button>
                                  </div>

                                  <Popover
                                    ref={popoverRef}
                                    placement="bottom-end"
                                    isOpen={popoverOpen === menu?.id}
                                    target={`popover-${menu?.id}`}
                                    toggle={() => togglePopover(menu?.id)}
                                  >
                                    <PopoverBody className="p-2">
                                      <div className="d-flex flex-column d-lg-none">
                                        <Button
                                          color="success"
                                          className="d-flex align-items-center w-100 m-0 mb-1"
                                          size="sm"
                                          onClick={() =>
                                            history.push(
                                              `/admin/menus/${menu?.id}/categories`
                                            )
                                          }
                                        >
                                          <i className="fa fa-list-alt mr-2" />
                                          Categories
                                        </Button>
                                        {exportAccess && (
                                          <Button
                                            className="bg-primary text-white d-flex align-items-center w-100 m-0 mb-1"
                                            size="sm"
                                            disabled={
                                              isExporting &&
                                              menu?.id === activeMenu?.id
                                            }
                                            onClick={() => {
                                              setActiveMenu(menu);
                                              handleExportMenu(menu);
                                            }}
                                          >
                                            {isExporting &&
                                              menu?.id === activeMenu?.id && (
                                                <Spinner
                                                  size="sm"
                                                  className="mr-2"
                                                />
                                              )}
                                            <i className="fa fa-file-export mr-2" />{" "}
                                            Export
                                          </Button>
                                        )}
                                        {editAccess && (
                                          <Button
                                            color="warning"
                                            className="d-flex align-items-center w-100 m-0 mb-1"
                                            size="sm"
                                            onClick={() =>
                                              initiateEditMenu(menu)
                                            }
                                          >
                                            <i className="fa fa-edit mr-2" />{" "}
                                            Edit
                                          </Button>
                                        )}
                                        {deleteAccess && (
                                          <Button
                                            color="danger"
                                            className="d-flex align-items-center w-100 m-0"
                                            size="sm"
                                            onClick={() => initiateDelete(menu)}
                                          >
                                            <i className="fa fa-trash mr-2" />{" "}
                                            Delete
                                          </Button>
                                        )}
                                      </div>
                                    </PopoverBody>
                                  </Popover>
                                </div>
                              </div>
                            </Col>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          ) : (
            <Row className="py-5 justify-content-center align-items-center">
              No Menus Found
            </Row>
          )}
        </div>
      </DragDropContext>
      <MenuModal
        isOpen={menuModal}
        toggle={menuModalToggler}
        editMode={editMode}
        activeMenu={activeMenu}
      />
      <ConfirmationModal
        title="Delete Menu"
        text={
          <>
            <h3>Are you sure you want to delete this menu?</h3>
            <span style={{ fontSize: "14px" }}>
              <strong>Note: </strong>
              This will delete categories and items against this menu.
            </span>
          </>
        }
        isOpen={confirmationModal}
        toggle={confirmationModalToggler}
        isLoading={isDeleting}
        onConfirmation={handleDeleteMenu}
      />
      <ItemModal
        isOpen={itemModal}
        toggle={itemModalToggler}
        quickItem={true}
        menuOptions={menuOptions}
        handleMenuSelect={handleMenuSelect}
        selectedMenu={selectedMenu}
        categoryOptions={categoryOpt}
        loadingCategories={loadingCategories}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        error={error}
        setError={setError}
      />
    </>
  );
};

export default Menu;
